import React, { useState, Suspense, lazy } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Music, Menu, X, Mail, User, LogOut, LayoutDashboard, FileMusic, Settings, Home } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import LanguageSelector from './LanguageSelector';
const ContactModal = lazy(() => import('./ContactModal'));

/**
 * 🎵 GLOBAL NAVBAR - VERSÃO ATUAL
 * 
 * Barra de navegação que aparece em TODAS as páginas do site.
 * MOBILE: Links aparecem em barra com scroll horizontal
 * DESKTOP: Links aparecem normalmente na barra
 */

const GlobalNavbar = () => {
  const [mobileMenu, setMobileMenu] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const { t } = useTranslation();
  const { user, isAuthenticated, logout, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  
  const isLoggedIn = isAuthenticated && user && !loading;

  const handleLandingLink = (e, sectionId) => {
    if (location.pathname === '/') {
      e.preventDefault();
      document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
    navigate('/');
  };

  return (
    <>
      <nav className="fixed w-full top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200/50 shadow-sm">
        {/* Barra Principal */}
        <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0" title="Ir para página inicial">
              <img 
                src="/logo-mmr.webp" 
                alt="MMR" 
                className="h-8 sm:h-10 w-auto"
              />
            </Link>

            {/* Menu Desktop - md e maiores */}
            <div className="hidden md:flex items-center space-x-1 lg:space-x-3 xl:space-x-5">
              <Link to="/" className="text-gray-700 hover:text-blue-600 transition-colors font-medium text-xs lg:text-sm flex items-center gap-1">
                <Home className="w-3 h-3" />
                Início
              </Link>
              <Link 
                to="/#como-funciona" 
                onClick={(e) => handleLandingLink(e, 'como-funciona')}
                className="text-gray-700 hover:text-blue-600 transition-colors font-medium text-xs lg:text-sm"
              >
                Como Funciona
              </Link>
              <Link 
                to="/#comparativo"
                onClick={(e) => handleLandingLink(e, 'comparativo')}
                className="text-gray-700 hover:text-blue-600 transition-colors font-medium text-xs lg:text-sm"
              >
                Comparativo
              </Link>
              <Link 
                to="/#pricing"
                onClick={(e) => handleLandingLink(e, 'pricing')}
                className="text-gray-700 hover:text-blue-600 transition-colors font-medium text-xs lg:text-sm"
              >
                Planos
              </Link>
              <Link to="/tendencias" className="text-gray-700 hover:text-orange-600 transition-colors font-medium text-xs lg:text-sm">
                🔥 Top 50
              </Link>
              <Link to="/gallery" className="text-gray-700 hover:text-blue-600 transition-colors font-medium text-xs lg:text-sm">
                📻 Rádio
              </Link>
              <Link to="/gallery/lyrics" className="text-gray-700 hover:text-purple-600 transition-colors font-medium text-xs lg:text-sm">
                📄 Letras
              </Link>
              <Link to="/artigos" className="text-gray-700 hover:text-blue-600 transition-colors font-medium text-xs lg:text-sm">
                📚 Artigos
              </Link>
              <button
                onClick={() => setShowContactModal(true)}
                data-testid="navbar-contact-btn"
                className="text-gray-700 hover:text-blue-600 transition-colors font-medium text-xs lg:text-sm flex items-center gap-1"
              >
                <Mail className="w-3 h-3 lg:w-4 lg:h-4" />
                Contato
              </button>
              
              <LanguageSelector />
              
              {/* Área do Usuário */}
              {isLoggedIn ? (
                <div className="relative">
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    data-testid="navbar-user-menu-btn"
                    className="flex items-center gap-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-3 py-1.5 rounded-lg hover:shadow-lg transition-all font-medium text-xs lg:text-sm"
                  >
                    <User className="w-3 h-3 lg:w-4 lg:h-4" />
                    <span className="max-w-20 truncate">{user.artistic_name || user.name?.split(' ')[0] || 'Usuário'}</span>
                  </button>
                  
                  {showUserMenu && (
                      <div
                        data-testid="navbar-user-dropdown"
                        className="animate-in fade-in slide-in-from-top-2 duration-200 absolute right-0 mt-2 w-52 bg-white rounded-xl shadow-2xl border border-gray-100 py-2 z-50"
                      >
                        <div className="px-4 py-2 border-b border-gray-100">
                          <p className="text-sm font-semibold text-gray-900 truncate">{user.name}</p>
                          <p className="text-xs text-gray-500 truncate">{user.email}</p>
                        </div>
                        <Link to="/" onClick={() => setShowUserMenu(false)} className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-green-50 hover:text-green-600 transition-colors text-sm">
                          <Home className="w-4 h-4" />
                          Início
                        </Link>
                        <Link to="/dashboard" onClick={() => setShowUserMenu(false)} className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors text-sm">
                          <LayoutDashboard className="w-4 h-4" />
                          Meu Painel
                        </Link>
                        <Link to="/works" onClick={() => setShowUserMenu(false)} className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors text-sm">
                          <FileMusic className="w-4 h-4" />
                          Minhas Obras
                        </Link>
                        <Link to="/profile" onClick={() => setShowUserMenu(false)} className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors text-sm">
                          <Settings className="w-4 h-4" />
                          Meu Perfil
                        </Link>
                        <div className="border-t border-gray-100 mt-1 pt-1">
                          <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 transition-colors w-full text-sm">
                            <LogOut className="w-4 h-4" />
                            Sair
                          </button>
                        </div>
                      </div>
                    )}
                </div>
              ) : (
                <>
                  <Link to="/login" className="text-gray-700 hover:text-blue-600 transition-colors font-medium text-xs lg:text-sm">
                    Entrar
                  </Link>
                  <Link to="/register" className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-3 lg:px-4 py-1.5 rounded-lg hover:shadow-lg transition-all font-semibold text-xs lg:text-sm">
                    Cadastrar
                  </Link>
                </>
              )}
            </div>

            {/* Botões Mobile - Idioma + Menu */}
            <div className="flex md:hidden items-center gap-2">
              <LanguageSelector />
              {isLoggedIn ? (
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-2 rounded-lg"
                >
                  <User className="w-5 h-5" />
                </button>
              ) : (
                <Link to="/login" className="text-blue-600 font-medium text-sm">
                  Entrar
                </Link>
              )}
              <button
                onClick={() => setMobileMenu(!mobileMenu)}
                data-testid="navbar-mobile-menu-btn"
                className="text-gray-700 hover:text-blue-600 p-1"
              >
                {mobileMenu ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
        
        {/* 🔥 BARRA DE NAVEGAÇÃO MOBILE - Com scroll horizontal e setas */}
        <div className="md:hidden relative border-t border-gray-100 bg-gray-50">
          {/* Seta Esquerda */}
          <div className="absolute left-0 top-0 bottom-0 w-6 bg-gradient-to-r from-gray-50 to-transparent z-10 flex items-center justify-start pointer-events-none">
            <span className="text-gray-400 text-xs animate-pulse">◀</span>
          </div>
          
          {/* Container com scroll */}
          <div className="overflow-x-auto scrollbar-hide">
            <div className="flex items-center space-x-1 px-6 py-2 min-w-max">
              <Link 
                to="/" 
                className="flex items-center gap-1 px-3 py-1.5 bg-green-100 text-green-700 rounded-full text-xs font-medium whitespace-nowrap"
              >
                <Home className="w-3 h-3" />
                Início
              </Link>
              <Link 
                to="/#como-funciona"
                onClick={(e) => handleLandingLink(e, 'como-funciona')}
                className="px-3 py-1.5 bg-white text-gray-700 rounded-full text-xs font-medium whitespace-nowrap border border-gray-200"
              >
                Como Funciona
              </Link>
              <Link 
                to="/#pricing"
                onClick={(e) => handleLandingLink(e, 'pricing')}
                className="px-3 py-1.5 bg-white text-gray-700 rounded-full text-xs font-medium whitespace-nowrap border border-gray-200"
              >
                Planos
              </Link>
              <Link 
                to="/tendencias"
                className="px-3 py-1.5 bg-orange-100 text-orange-700 rounded-full text-xs font-medium whitespace-nowrap"
              >
                🔥 Top 50
              </Link>
              <Link 
                to="/gallery"
                className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium whitespace-nowrap"
              >
                📻 Rádio
              </Link>
              <Link 
                to="/gallery/lyrics"
                className="px-3 py-1.5 bg-purple-100 text-purple-700 rounded-full text-xs font-medium whitespace-nowrap"
              >
                📄 Letras
              </Link>
              <Link 
                to="/artigos"
                className="px-3 py-1.5 bg-white text-gray-700 rounded-full text-xs font-medium whitespace-nowrap border border-gray-200"
              >
                📚 Artigos
              </Link>
              <button
                onClick={() => setShowContactModal(true)}
                className="px-3 py-1.5 bg-white text-gray-700 rounded-full text-xs font-medium whitespace-nowrap border border-gray-200"
              >
                ✉️ Contato
              </button>
              {!isLoggedIn && (
                <Link 
                  to="/register"
                  className="px-3 py-1.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-full text-xs font-semibold whitespace-nowrap"
                >
                  Cadastrar
                </Link>
              )}
            </div>
          </div>
          
          {/* Seta Direita */}
          <div className="absolute right-0 top-0 bottom-0 w-6 bg-gradient-to-l from-gray-50 to-transparent z-10 flex items-center justify-end pointer-events-none">
            <span className="text-gray-400 text-xs animate-pulse">▶</span>
          </div>
        </div>
        
        {/* Banner de certificado */}
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white text-center py-1 text-xs">
          <span className="font-medium">🏆 Certificado reconhecido em 180+ países com validade jurídica</span>
        </div>
      </nav>

      {/* Menu Mobile Expandido (quando clica no hamburger) */}
      {mobileMenu && (
          <>
            <div
              onClick={() => setMobileMenu(false)}
              data-testid="navbar-mobile-backdrop"
              className="animate-in fade-in duration-200 fixed inset-0 bg-black/50 z-[80] md:hidden"
            />
            
            <div
              data-testid="navbar-mobile-panel"
              className="animate-in slide-in-from-right duration-300 fixed inset-y-0 right-0 w-72 bg-white shadow-2xl z-[90] md:hidden overflow-y-auto"
            >
              <div className="flex justify-end px-4 pt-4">
                <button
                  onClick={() => setMobileMenu(false)}
                  aria-label="Fechar menu"
                  data-testid="navbar-mobile-close-btn"
                  className="p-2 rounded-full text-gray-600 hover:bg-gray-100 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="flex flex-col px-6 pb-6 space-y-3">
                {isLoggedIn && (
                  <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-4 mb-2">
                    <p className="font-semibold text-gray-900">{user.name}</p>
                    <p className="text-sm text-gray-500">{user.email}</p>
                    <div className="flex gap-2 mt-3">
                      <Link to="/" onClick={() => setMobileMenu(false)} className="flex-1 bg-green-600 text-white text-center py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-1">
                        <Home className="w-4 h-4" />
                        Início
                      </Link>
                      <Link to="/dashboard" onClick={() => setMobileMenu(false)} className="flex-1 bg-blue-600 text-white text-center py-2 rounded-lg text-sm font-medium">
                        Painel
                      </Link>
                    </div>
                  </div>
                )}
                
                <Link to="/" onClick={() => setMobileMenu(false)} className="text-green-600 hover:text-green-700 font-medium py-2 flex items-center gap-2 bg-green-50 rounded-lg px-3">
                  <Home className="w-5 h-5" />
                  Início (Home)
                </Link>
                
                <Link to="/#como-funciona" onClick={(e) => { setMobileMenu(false); handleLandingLink(e, 'como-funciona'); }} className="text-gray-700 hover:text-blue-600 font-medium py-2">
                  Como Funciona
                </Link>
                <Link to="/#comparativo" onClick={(e) => { setMobileMenu(false); handleLandingLink(e, 'comparativo'); }} className="text-gray-700 hover:text-blue-600 font-medium py-2">
                  Comparativo
                </Link>
                <Link to="/#pricing" onClick={(e) => { setMobileMenu(false); handleLandingLink(e, 'pricing'); }} className="text-gray-700 hover:text-blue-600 font-medium py-2">
                  Planos
                </Link>
                <Link to="/tendencias" onClick={() => setMobileMenu(false)} className="text-gray-700 hover:text-orange-600 font-medium py-2">
                  🔥 Top 50 Brasil
                </Link>
                <Link to="/gallery" onClick={() => setMobileMenu(false)} className="text-gray-700 hover:text-blue-600 font-medium py-2">
                  📻 Rádio do Compositor
                </Link>
                <Link to="/gallery/lyrics" onClick={() => setMobileMenu(false)} className="text-gray-700 hover:text-purple-600 font-medium py-2">
                  📄 Galeria de Letras
                </Link>
                <Link to="/artigos" onClick={() => setMobileMenu(false)} className="text-gray-700 hover:text-blue-600 font-medium py-2">
                  📚 Artigos
                </Link>
                <button onClick={() => { setMobileMenu(false); setShowContactModal(true); }} className="text-gray-700 hover:text-blue-600 font-medium flex items-center gap-2 py-2">
                  <Mail className="w-4 h-4" />
                  Fale Conosco
                </button>
                
                <div className="border-t border-gray-200 pt-4 mt-2">
                  {isLoggedIn ? (
                    <>
                      <Link to="/works" onClick={() => setMobileMenu(false)} className="flex items-center gap-3 text-gray-700 hover:text-blue-600 font-medium py-2">
                        <FileMusic className="w-5 h-5" />
                        Minhas Obras
                      </Link>
                      <Link to="/profile" onClick={() => setMobileMenu(false)} className="flex items-center gap-3 text-gray-700 hover:text-blue-600 font-medium py-2">
                        <Settings className="w-5 h-5" />
                        Meu Perfil
                      </Link>
                      <button onClick={() => { setMobileMenu(false); handleLogout(); }} className="flex items-center gap-3 text-red-600 hover:text-red-700 font-medium py-2 w-full">
                        <LogOut className="w-5 h-5" />
                        Sair
                      </button>
                    </>
                  ) : (
                    <>
                      <Link to="/login" onClick={() => setMobileMenu(false)} className="text-gray-700 hover:text-blue-600 font-medium block py-2">
                        Entrar
                      </Link>
                      <Link to="/register" onClick={() => setMobileMenu(false)} className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl text-center font-semibold block mt-2">
                        Cadastrar
                      </Link>
                    </>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      
      {/* User Menu Mobile Dropdown */}
        {showUserMenu && isLoggedIn && (
          <>
            <div
              onClick={() => setShowUserMenu(false)}
              className="fixed inset-0 z-40 md:hidden"
            />
            <div
              className="animate-in fade-in slide-in-from-top-2 duration-200 fixed top-16 right-2 w-56 bg-white rounded-xl shadow-2xl border border-gray-100 py-2 z-50 md:hidden"
            >
              <div className="px-4 py-2 border-b border-gray-100">
                <p className="text-sm font-semibold text-gray-900 truncate">{user.name}</p>
                <p className="text-xs text-gray-500 truncate">{user.email}</p>
              </div>
              <Link to="/" onClick={() => setShowUserMenu(false)} className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-green-50 hover:text-green-600 transition-colors text-sm">
                <Home className="w-4 h-4" />
                Início
              </Link>
              <Link to="/dashboard" onClick={() => setShowUserMenu(false)} className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors text-sm">
                <LayoutDashboard className="w-4 h-4" />
                Meu Painel
              </Link>
              <Link to="/works" onClick={() => setShowUserMenu(false)} className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors text-sm">
                <FileMusic className="w-4 h-4" />
                Minhas Obras
              </Link>
              <Link to="/profile" onClick={() => setShowUserMenu(false)} className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors text-sm">
                <Settings className="w-4 h-4" />
                Meu Perfil
              </Link>
              <div className="border-t border-gray-100 mt-1 pt-1">
                <button onClick={() => { setShowUserMenu(false); handleLogout(); }} className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 transition-colors w-full text-sm">
                  <LogOut className="w-4 h-4" />
                  Sair
                </button>
              </div>
            </div>
          </>
        )}
      
      {showContactModal && (
        <Suspense fallback={null}>
          <ContactModal isOpen={showContactModal} onClose={() => setShowContactModal(false)} />
        </Suspense>
      )}
    </>
  );
};

export default GlobalNavbar;
